import { memo, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'

import { TPopupHorizontal, TPopupVertical } from 'core-components/library/popup'

import { FilterPanelButton } from './components/addFilterButton'
import { FilterChip } from './components/filterChip'
import { OverflowChips } from './components/overflowChips'
import { ActiveFiltersMap, FilterConfig, FilterValue } from './types'

import styles from './DataFilterBar.module.scss'

type Props = {
  config: FilterConfig[]
  value: ActiveFiltersMap
  onChange: (filters: ActiveFiltersMap) => void
  className?: string
  /** If true, shows active filters as chips next to the button */
  showChips?: boolean
  /** Button text. Default: "Filter" */
  buttonText?: string
  /** Button color. Default: "primary" */
  buttonColor?: 'grey' | 'white' | 'primary' | 'success' | 'warning' | 'danger'
  /** Button variant. Default: "outlined" */
  buttonVariant?: 'contained' | 'outlined' | 'text'
  /** Button size. Default: "sm" */
  buttonSize?: 'sm' | 'md'
  /** Button icon class. Default: "kbase kbase-filter" */
  buttonIcon?: string
  /** Button icon position. Default: "start" */
  buttonIconPosition?: 'end' | 'start'
  /** Additional button className */
  buttonClassName?: string
  /** Additional className for popup root element */
  popupRootClassName?: string
  /** Additional className for popup panel */
  popupClassName?: string
  /** Use fixed positioning for popup (for scrollable containers). Default: false */
  useFixedPosition?: boolean
  /** Horizontal start position for popup. Default: Right (popup opens to the right of button) */
  horizontalStartPosition?: TPopupHorizontal
  /** Horizontal direction for popup. Default: Right */
  horizontalDirection?: TPopupHorizontal
  /** Vertical start position for popup. Default: Top */
  verticalStartPosition?: TPopupVertical
  /** Max height of the filter panel. Can be a number (px) or string (e.g. '500px', '50vh') */
  maxHeight?: string | number
  /** Maximum number of visible chips. Remaining chips will be shown in overflow menu. If not set, auto-calculated based on container width. */
  maxVisibleChips?: number
  /** Enable search field in filter panel header when number of filters exceeds threshold. Default: false */
  enableFilterSearch?: boolean
  /** Minimum number of filters to show search field. Default: 5 */
  filterSearchThreshold?: number
  /** Filter panel title. Default: "Filter" */
  filterPanelTitle?: string
  /** Filter panel search placeholder. Default: "Search filters..." */
  filterPanelSearchPlaceholder?: string
  /** Filter panel reset button text. Default: "Reset" */
  filterPanelResetText?: string
  /** Filter panel apply button text. Default: "Apply Now" */
  filterPanelApplyText?: string
}

// Debounce helper
const debounce = <T extends (...args: Parameters<T>) => void>(fn: T, ms: number) => {
  let timeoutId: ReturnType<typeof setTimeout>
  return (...args: Parameters<T>) => {
    clearTimeout(timeoutId)
    timeoutId = setTimeout(() => fn(...args), ms)
  }
}

export const DataFilterBar = memo<Props>(
  ({
    config,
    value,
    onChange,
    className,
    showChips = true,
    buttonText,
    buttonVariant,
    buttonSize,
    buttonIcon,
    buttonIconPosition,
    buttonColor,
    buttonClassName,
    popupRootClassName,
    popupClassName,
    useFixedPosition = false,
    horizontalStartPosition,
    horizontalDirection,
    verticalStartPosition,
    maxHeight,
    maxVisibleChips,
    enableFilterSearch,
    filterSearchThreshold,
    filterPanelTitle,
    filterPanelSearchPlaceholder,
    filterPanelResetText,
    filterPanelApplyText,
  }) => {
    const containerRef = useRef<HTMLDivElement>(null)
    const measureRef = useRef<HTMLDivElement>(null)
    const [calculatedMaxChips, setCalculatedMaxChips] = useState<number>(Object.keys(value).length)
    const lastContainerWidthRef = useRef<number>(0)

    // Get config for a filter by id
    const getConfigById = useCallback(
      (id: string): FilterConfig | undefined => {
        return config.find((c) => c.id === id)
      },
      [config]
    )

    // Update a single filter's value (for chip editing)
    const handleChipChange = useCallback(
      (filterId: string, newValue: FilterValue) => {
        onChange({ ...value, [filterId]: newValue })
      },
      [value, onChange]
    )

    // Remove a filter (from chip)
    const handleChipRemove = useCallback(
      (filterId: string) => {
        onChange(Object.fromEntries(Object.entries(value).filter(([key]) => key !== filterId)))
      },
      [value, onChange]
    )

    // Convert filters map to array for iteration
    const filtersArray = Object.entries(value).map(([id, filterValue]) => ({
      id,
      value: filterValue,
    }))
    const filtersCount = filtersArray.length

    // Calculate visible chips based on container width
    const calculateVisibleChips = useCallback(() => {
      if (maxVisibleChips !== undefined) return

      const container = containerRef.current
      const measureContainer = measureRef.current

      if (!container || !measureContainer || filtersCount === 0) {
        setCalculatedMaxChips(filtersCount)
        return
      }

      const containerWidth = container.offsetWidth
      // Skip if width hasn't changed significantly (within 5px tolerance)
      if (Math.abs(containerWidth - lastContainerWidthRef.current) < 5) {
        return
      }
      lastContainerWidthRef.current = containerWidth

      const children = measureContainer.children
      const gap = 8
      const overflowButtonWidth = 45 // "+N" button width
      const buttonElement = container.querySelector('[class*="FilterPanelButton"]') as HTMLElement
      const filterButtonWidth = buttonElement?.offsetWidth || 80

      let totalWidth = filterButtonWidth + gap
      let visibleCount = 0

      for (let i = 0; i < children.length; i++) {
        const child = children[i] as HTMLElement
        if (!child.offsetWidth) continue

        const childWidth = child.offsetWidth + gap
        const needsOverflow = i < filtersCount - 1 // Not the last chip

        if (totalWidth + childWidth + (needsOverflow ? overflowButtonWidth : 0) <= containerWidth) {
          totalWidth += childWidth
          visibleCount++
        } else {
          break
        }
      }

      setCalculatedMaxChips(Math.max(1, visibleCount))
    }, [maxVisibleChips, filtersCount])

    // Initial calculation and recalculate on value change
    useLayoutEffect(() => {
      if (maxVisibleChips !== undefined) return
      // Reset to show all, then calculate
      setCalculatedMaxChips(filtersCount)
      // Use requestAnimationFrame to measure after render
      requestAnimationFrame(() => {
        calculateVisibleChips()
      })
    }, [filtersCount, maxVisibleChips, calculateVisibleChips])

    // Handle resize with debounce
    useEffect(() => {
      if (maxVisibleChips !== undefined) return

      const debouncedCalculate = debounce(() => {
        lastContainerWidthRef.current = 0 // Reset to force recalculation
        calculateVisibleChips()
      }, 150)

      const resizeObserver = new ResizeObserver(debouncedCalculate)
      if (containerRef.current) {
        resizeObserver.observe(containerRef.current)
      }

      return () => resizeObserver.disconnect()
    }, [maxVisibleChips, calculateVisibleChips])

    // Determine effective max chips
    const effectiveMaxChips = maxVisibleChips ?? calculatedMaxChips

    // Split filters into visible and overflow
    const visibleFilters =
      filtersCount > effectiveMaxChips ? filtersArray.slice(0, effectiveMaxChips) : filtersArray
    const overflowFilters =
      filtersCount > effectiveMaxChips ? filtersArray.slice(effectiveMaxChips) : []

    return (
      <div className={`${styles.root} ${className || ''}`} ref={containerRef}>
        <FilterPanelButton
          config={config}
          value={value}
          onChange={onChange}
          buttonText={buttonText}
          buttonColor={buttonColor}
          buttonVariant={buttonVariant}
          buttonSize={buttonSize}
          buttonIcon={buttonIcon}
          buttonIconPosition={buttonIconPosition}
          buttonClassName={buttonClassName}
          rootClassName={popupRootClassName}
          popupClassName={popupClassName}
          useFixedPosition={useFixedPosition}
          horizontalStartPosition={horizontalStartPosition}
          horizontalDirection={horizontalDirection}
          verticalStartPosition={verticalStartPosition}
          maxHeight={maxHeight}
          enableFilterSearch={enableFilterSearch}
          filterSearchThreshold={filterSearchThreshold}
          filterPanelTitle={filterPanelTitle}
          filterPanelSearchPlaceholder={filterPanelSearchPlaceholder}
          filterPanelResetText={filterPanelResetText}
          filterPanelApplyText={filterPanelApplyText}
        />

        {showChips && filtersCount > 0 && (
          <div className={styles.chips}>
            {visibleFilters.map((filter) => {
              const filterConfig = getConfigById(filter.id)
              if (!filterConfig) return null

              return (
                <FilterChip
                  key={filter.id}
                  config={filterConfig}
                  value={filter.value}
                  onChange={(newValue) => handleChipChange(filter.id, newValue)}
                  onRemove={() => handleChipRemove(filter.id)}
                />
              )
            })}
            {overflowFilters.length > 0 && (
              <OverflowChips
                filters={overflowFilters}
                config={config}
                onFilterChange={handleChipChange}
                onFilterRemove={handleChipRemove}
              />
            )}
          </div>
        )}

        {/* Hidden container for measuring all chips */}
        {showChips && maxVisibleChips === undefined && filtersCount > 0 && (
          <div className={styles.measureContainer} ref={measureRef} aria-hidden="true">
            {filtersArray.map((filter) => {
              const filterConfig = getConfigById(filter.id)
              if (!filterConfig) return null

              return (
                <FilterChip
                  key={filter.id}
                  config={filterConfig}
                  value={filter.value}
                  onChange={() => {}}
                  onRemove={() => {}}
                />
              )
            })}
          </div>
        )}
      </div>
    )
  }
)

DataFilterBar.displayName = 'DataFilterBar'
